import React from "react";
import Helmet from "react-helmet";
import { graphql } from "gatsby";

import PagesTemplate from "../_template";
import BorderButton from "../../components/BorderButton";

export const query = graphql`
  {
    site {
      siteMetadata {
        siteTitle
        loginLink
        signupLink
      }
    }
  }
`;

export default class Features extends React.Component {
  render() {
    return (
      <PagesTemplate>
        <div>
          <Helmet
            title={`Outreach Features | ${this.props.data.site.siteMetadata.siteTitle}`}
            meta={[
              {
                name: "description",
                content:
                  "O3 creates tools to help business measure customer and patient satisfaction and performance",
              },
              {
                name: "keywords",
                content:
                  "O3, outreach, perspexi labs, business analytics, customer satisfaction, customer experience, text message surveys, digital health, patient satisfaction, outcome measures",
              },
            ]}
          />

          <h2
            className="f1 lh-title tc mw8 mt2 center pv4"
            style={{ color: "#00a5b5" }}
          >
            So easy you could forget about it.
            <br />
            So impactful you never will.
          </h2>

          <h3 className="f2 lh-solid tc mw8 center">Outreach™ by O3</h3>

          {/* Video Demo Here! */}

          <section className="cf pv4 tc">
            <div className="mw8 center">
              <div className="fl-ns w-third-ns ph4 grow">
                <img
                  src="/img/paper-airplane-2x.png"
                  alt="Put in a customer number and forget about it"
                  style={{ maxHeight: 128, maxWidth: 128 }}
                />
                <h3 className="f4 lh-title">
                  Auto-send texts after customer interactions
                </h3>
                <span className="f5 lh-copy db mv2">
                  Enter the customer&apos;s mobile number at the end of a visit
                  and Outreach will automatically schedule and send a text after
                  an appropriate delay.
                </span>
              </div>
              <div className="fl-ns w-third-ns ph4 grow">
                <img
                  src="/img/woman-2x.png"
                  alt="Text chat conversations with customers"
                  style={{ maxHeight: 128, maxWidth: 128 }}
                />
                <h3 className="f4 lh-title">
                  Customer responds via simple text messages
                </h3>
                <span className="f5 lh-copy db mv2">
                  The customers respond with their satisfaction responses
                  directly via SMS, and Outreach collects and analyzes the
                  results.
                </span>
              </div>
              <div className="fl-ns w-third-ns ph4 grow">
                <img
                  src="/img/dashboard-2x.png"
                  alt="Real-time dashboards show you feedback as it arrives"
                  style={{ maxHeight: 128, maxWidth: 128 }}
                />
                <h3 className="f4 lh-title">
                  Scores are instantly updated and available
                </h3>
                <span className="f5 lh-copy db mv2">
                  Get notified immediately of any negative customer experiences
                  and take action right away to turn a detractor into a
                  promoter.
                </span>
              </div>
            </div>
          </section>

          <section className="tc pt3 pb5">
            <BorderButton
              label="Start your Free Trial"
              link={this.props.data.site.siteMetadata.signupLink}
              primary
              color="blue"
            />
            <BorderButton label="See Pricing" link="/outreach/pricing/" />
          </section>

          <hr className="mw9" />

          <section className="mw8 center pv3 ph2 cf">
            <img
              src="/img/screenshots/main-dashboard.png"
              className="w-100 w-two-thirds-ns fr"
              alt="Main dashboard of O3 Outreach, showing NPS score, text survey responses, and charts"
            />
            <div className="w-100 w-third-ns pr4">
              <h3 className="f3 lh-title">
                Did you know that customers are 7.5&times; more likely to
                respond to a text than an email?
              </h3>
              <p className="f5 lh-copy">
                O3&apos;s Outreach uses transactional text messages (as opposed
                to marketing text messages) that form a part of your interaction
                with your customer and close the loop. When sent within a few
                hours of your service,{" "}
                <strong>
                  response rates of transactional texts average 45%, blowing
                  past both email and phone,
                </strong>{" "}
                and are more convenient and less intrusive for your customers.
              </p>
            </div>
          </section>

          <hr className="mw9" />

          {/* NPS Surveys Built In */}

          <section className="mw7 center ph2 pt4">
            <img
              src="/img/nps-convo.png"
              alt="Displays a text message conversation between O3's Outreach and a customer asking them for a rating on a 0 to 10 NPS scale"
              className="mw5 fr pl3"
            />

            <h3>NPS surveys built in</h3>

            <p className="f5 lh-copy">
              Using the Net Promoter Score<sup>®</sup> system gives you one easy
              number to track your customers&apos; satisfaction and loyalty
              levels, as well as their likelihood of referring you to their
              friends and colleagues.
            </p>
            <p className="f5 lh-copy">
              The NPS<sup>®</sup> survey is built into Outreach and is the
              default survey, making it incredibly easy to start collecting and
              acting on customer satisfaction data right away. Our main
              dashboard tracks your score and gives you easy access to see
              direct customer comments.
            </p>
          </section>

          {/* Notifications for detractors */}

          <section className="cr mw7 center ph2 pt4 cf">
            <h3>Instant alerts of customer issues</h3>

            <img
              src="/img/screenshots/detractor-alert-email.png"
              className="w-50 fl pr3"
              alt="An email alert from O3's Outreach showing a detractor score and reason"
            />
            <p className="f5 lh-copy">
              As soon as Outreach detects that a customer had a negative
              experience, you are alerted immediately both via the dashboard and
              by an email message, along with the customer&apos;s score,
              comments, and contact info.
            </p>
            <p className="f5 lh-copy">
              By providing the means to close the feedback loop and get right
              back to the customer, you can turn a customer with a negative
              experience into a happy promoter. This is how you blow retention
              through the roof.
            </p>
          </section>

          {/* Track per-employee performance */}

          <section className="mw7 center ph2 pt4 cf">
            <h3>Track per-employee performance</h3>

            <img
              src="/img/screenshots/person-filter.png"
              className="w-60 fr pl3"
              alt="Filter capabilities of O3 Outreach"
            />
            <p className="f5 lh-copy">
              You&apos;ve built a company and hired as you&apos;ve grown, but
              now you&apos;re a step removed from those customers that are the
              lifeblood of your business. By tracking which employees are
              interacting with which customers, you can link customer
              satisfaction back to experiences with individual employees.
            </p>
            <p className="f5 lh-copy">
              With customers often providing direct feedback on the unique
              person they interacted with, you gain powerful insight into each
              employee&apos;s performance, strengths, and opportunities to
              improve, and puts you back in touch with the front line of your
              business.
            </p>
          </section>

          {/* Garner reviews and remind promoters of promos */}

          <section className="mw7 center ph2 pt4 cf">
            <h3>Garner reviews and remind promoters of promos</h3>

            <img
              src="/img/screenshots/leave-a-review-chat.png"
              className="w-30 pr3 fl"
              alt="Chat showing a review link"
            />
            <p className="f5 lh-copy">
              Getting extremely positive feedback? Outreach by O3 allows you to
              specifically target your most loyal customers (those giving you an
              NPS score of 9 or 10) and complete the loop with a link to leave a
              review for you online or a reminder of any offers or promos you
              may be having (for example, referral bonuses).
            </p>
            <p className="f5 lh-copy">
              Customer acquisition is always a challenge, but how much easier
              will it be when your customers actually know about your referral
              offers or are actively being funneled into leaving their positive
              experiences in online reviews? Hint: a lot easier!
            </p>
          </section>

          {/* Custom campaigns for your unique needs */}

          <section className="mw7 center ph2 pt4">
            <h3>Customer campaigns for your unique needs</h3>

            <p className="f5 lh-copy">
              Want to simply gather very targeted feedback from a subset of your
              customers without asking for a rating? Or would you like to poll
              your customer base via text message? You can create your own
              custom text campaigns to suit your needs.
            </p>
          </section>

          {/* Intermixed here would be all the little features too that don't warrant their own section, but are good nonetheless */}

          <section className="mw6 center ph2 pt4">
            <h4>Other features</h4>
            <ul>
              <li>Built for the most demanding security needs</li>
              <li>Ready to grow to your needs</li>
              <li>Unlimited users, employees, and admins</li>
              <li>Upload phone numbers in bulk for sending</li>
            </ul>
          </section>

          <section className="tc pv4">
            <BorderButton label="See Pricing" link="/outreach/pricing/" />
            <BorderButton
              label="Start your 7-day Free Trial Now"
              link={this.props.data.site.siteMetadata.signupLink}
              primary
              color="blue"
            />
          </section>
        </div>
      </PagesTemplate>
    );
  }
}
